import React, { useEffect, useMemo, useRef, useState } from 'react';
import classnames from 'classnames';
import moment from 'moment';
import { api } from 'api';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import { useResizeDetector } from 'react-resize-detector';
import styles from './dayviewtimelinegrid.module.scss';
import { queryFreeBusyList } from '../../service';
import DateSwitch from './DateSwitch';
import { fixNumber } from './util';
import { rangeInteract } from '../TimeLinePicker/util';
import GridHeader from './GridHeader';
import { useAppSelector } from '@web-common/state/createStore';
import { isAlldayOrCrossEvent, sortByUser, transfer } from '../ScheduleModal/util';
import EventCell from '../ScheduleModal/EventCell';
import { getIn18Text } from 'api';
const sysApi = api.getSystemApi();
const timeLineScale = new Array(24).fill(null).map((_, index) => fixNumber(index + 1));
const DEFAULT_SIZE_OPTIONS = {
    DATE_SWTICH_HEIGHT: 60,
    MIN_GRID_CONTAINER_HEIGHT: 502,
    MIN_GRID_CONTAINER_WIDTH: 380,
    GRID_CONTAINER_MARGIN_RIGHT: 16,
    GRID_CONTAINER_MARGIN_BOTTOM: 0,
    GRID_COL_HEADER_WIDTH: 60,
    GRID_CELL_HEIGHT: 50,
    TIME_CELL_MARGIN_RIGHT: 5,
    TIME_CELL_MARGIN_LEFT: 2,
};
const defaultUsers = [];
const DayViewTimeLineGrid = ({ startDate, endDate, startTime, endTime, users = defaultUsers, allDay = 0, sizeOptions, onClose }) => {
    const { DATE_SWTICH_HEIGHT, MIN_GRID_CONTAINER_HEIGHT, MIN_GRID_CONTAINER_WIDTH, GRID_CONTAINER_MARGIN_RIGHT, GRID_CONTAINER_MARGIN_BOTTOM, GRID_COL_HEADER_WIDTH, GRID_CELL_HEIGHT, TIME_CELL_MARGIN_RIGHT, TIME_CELL_MARGIN_LEFT, } = Object.assign(Object.assign({}, DEFAULT_SIZE_OPTIONS), sizeOptions);
    const { scheduleEvent: editingEvent } = useAppSelector(state => state.scheduleReducer);
    const uid = editingEvent === null || editingEvent === void 0 ? void 0 : editingEvent.scheduleInfo.uid;
    const currentMoment = moment();
    const getInitFreeBusyList = () => {
        const organizer = editingEvent === null || editingEvent === void 0 ? void 0 : editingEvent.scheduleInfo.organizer;
        const currentUser = sysApi.getCurrentUser();
        if (organizer) {
            return [
                {
                    account: Object.assign({}, organizer),
                    freeBusyItems: [],
                },
            ];
        }
        if (currentUser) {
            return [
                {
                    account: {
                        accountId: currentUser.id,
                        extDesc: currentUser.id,
                        extNickname: currentUser.nickName,
                    },
                    freeBusyItems: [],
                },
            ];
        }
        return [];
    };
    const [currentDate, setCurrentDate] = useState(startDate);
    const [userFreeBusy, setUserFreeBusy] = useState(getInitFreeBusyList());
    const [scrollOffset, setScrollOffset] = useState({ x: 0, y: 0 });
    const scrollRef = useRef(null);
    const { ref: sizeRef, width: containerWidth = MIN_GRID_CONTAINER_WIDTH, height: containerHeight = MIN_GRID_CONTAINER_HEIGHT } = useResizeDetector();
    const criticalCellWidth = useRef(0);
    const GRID_WIDTH = containerWidth - GRID_COL_HEADER_WIDTH - GRID_CONTAINER_MARGIN_RIGHT;
    const GRID_CONTAINER_HEIGHT = containerHeight - DATE_SWTICH_HEIGHT;
    const formStart = useMemo(() => {
        const _startDate = startDate;
        const _startTime = startTime;
        if (_startDate && _startTime) {
            return allDay
                ? _startDate.clone().startOf('day')
                : _startDate.clone().set({
                    hour: _startTime.hours(),
                    minute: _startTime.minute(),
                    second: 0,
                    millisecond: 0,
                });
        }
        return null;
    }, [allDay, startDate, startTime]);
    const formEnd = useMemo(() => {
        const _endDate = endDate;
        const _endTime = endTime;
        if (_endDate && _endTime) {
            return allDay
                ? _endDate.clone().endOf('day')
                : _endDate.clone().set({
                    hour: _endTime.hours(),
                    minute: _endTime.minute(),
                    second: 0,
                    millisecond: 0,
                });
        }
        return null;
    }, [allDay, endDate, endTime]);
    const handleGridCellScroll = (e) => {
        setScrollOffset({
            x: e.target.scrollLeft,
            y: e.target.scrollTop,
        });
    };
    const GRID_HEADER_HEIGHT = useMemo(() => {
        const height = GRID_CELL_HEIGHT;
        let maxEventsNum = 0;
        userFreeBusy.forEach(i => {
            const allDayAndCrossEvents = i.freeBusyItems.filter(c => isAlldayOrCrossEvent(c));
            maxEventsNum = Math.max(maxEventsNum, allDayAndCrossEvents.length);
        });
        const num = maxEventsNum > 4 ? 4 : maxEventsNum;
        return num ? 45 + num * 20 : height;
    }, [userFreeBusy]);
    const GRID_CELL_WIDTH = useMemo(() => {
        let CW = GRID_WIDTH / (userFreeBusy.length || 1);
        if (userFreeBusy.length < 3) {
            criticalCellWidth.current = 0;
        }
        else {
            if (criticalCellWidth.current === 0) {
                criticalCellWidth.current = GRID_WIDTH / 2.5;
            }
            CW = Math.max(criticalCellWidth.current, CW);
        }
        return Math.ceil(CW);
    }, [GRID_WIDTH, userFreeBusy.length]) - 2;
    const indicatorRange = useMemo(() => rangeInteract([(formStart || currentMoment).clone(), (formEnd || currentMoment).clone()], [(currentDate || currentMoment).clone().startOf('day'), (currentDate || currentMoment).clone().endOf('day')]), [currentDate, formStart, formEnd]);
    const userBusyStatusArray = useMemo(() => userFreeBusy.map(item => {
        let busy = false;
        if (formStart && formEnd) {
            for (let i = 0; i < item.freeBusyItems.length; i++) {
                const element = item.freeBusyItems[i];
                busy = rangeInteract([moment(element.start), moment(element.end)], [formStart, formEnd]) !== null;
                if (busy) {
                    break;
                }
            }
        }
        return busy;
    }), [formStart, formEnd, userFreeBusy]);
    const renderIndicator = () => {
        if (indicatorRange === null) {
            return null;
        }
        return (React.createElement("div", { style: {
                position: 'absolute',
                zIndex: 99,
                padding: 2,
                width: GRID_CELL_WIDTH * userFreeBusy.length,
                height: indicatorRange[1].diff(indicatorRange[0].clone(), 'hours', !0) * GRID_CELL_HEIGHT,
                top: indicatorRange[0].diff(indicatorRange[0].clone().startOf('day'), 'hours', !0) * GRID_CELL_HEIGHT,
            } },
            React.createElement("div", { className: classnames(styles.indicator, {
                    [styles.indicatorBusy]: userBusyStatusArray.reduce((prev, busy) => prev || busy, false),
                }) })));
    };
    const isBusyEvent = (event) => {
        if (indicatorRange === null) {
            return false;
        }
        return rangeInteract([moment(event.start), moment(event.end)], indicatorRange) !== null;
    };
    const renderEvent = (allEvents) => {
        const events = allEvents.filter(e => !isAlldayOrCrossEvent(e));
        const myEvents = transfer(events, GRID_CELL_HEIGHT).map(i => {
            const isBusy = isBusyEvent(i);
            return Object.assign(Object.assign({}, i), { textColor: isBusy ? '#51555c' : '#a8aaad', color: isBusy ? 'rgba(247, 79, 79, 0.2)' : 'rgba(232, 232, 232, 0.6)' });
        });
        return (React.createElement(React.Fragment, null, myEvents.map(i => (React.createElement("div", { className: styles.eventTimeOuter, key: i.scheduleId, style: {
                top: i.top,
                left: i.left + '%',
                right: i.right + '%',
                height: i.height,
            } },
            React.createElement(EventCell, { event: i, judgePast: false }))))));
    };
    const renderBusyCell = () => (React.createElement(React.Fragment, null,
        React.createElement("div", { className: styles.timeContent, style: {
                width: GRID_CELL_WIDTH * userFreeBusy.length,
                height: GRID_CELL_HEIGHT * timeLineScale.length,
            } }, userFreeBusy.map(usr => (React.createElement("div", { key: usr.account.accountId, className: styles.timeContentIn, style: {
                width: GRID_CELL_WIDTH - TIME_CELL_MARGIN_RIGHT - TIME_CELL_MARGIN_LEFT,
                marginRight: TIME_CELL_MARGIN_RIGHT,
                marginLeft: TIME_CELL_MARGIN_LEFT,
            } }, renderEvent(usr.freeBusyItems)))))));
    const renderGridRowHeader = () => (React.createElement("div", { className: styles.gridRowHeaderContainer, style: {
            position: 'absolute',
            height: GRID_HEADER_HEIGHT,
            width: userFreeBusy.length * GRID_CELL_WIDTH + GRID_COL_HEADER_WIDTH,
            left: -scrollOffset.x,
        } },
        React.createElement("div", { className: styles.gridRowHeader, style: {
                position: 'absolute',
                left: GRID_COL_HEADER_WIDTH,
                height: GRID_HEADER_HEIGHT,
                width: GRID_CELL_WIDTH * userFreeBusy.length,
            } }, userFreeBusy.map((item, index) => (React.createElement("div", { key: item.account.accountId, className: styles.gridRowHeaderCell, style: {
                position: 'absolute',
                left: index * GRID_CELL_WIDTH,
                height: GRID_HEADER_HEIGHT,
                width: GRID_CELL_WIDTH,
            } },
            React.createElement(GridHeader, { indicatorRange: indicatorRange, events: item.freeBusyItems, busy: userBusyStatusArray[index], text: item.account.extNickname || item.account.extDesc })))))));
    const renderGirdColHeader = () => {
        const isToday = moment().isSame(currentDate, 'day');
        const dayStart = moment().startOf('day');
        const nowTop = moment().diff(dayStart, 'hour', true) * GRID_CELL_HEIGHT;
        return (React.createElement("div", { className: styles.gridColHaederContainer, style: {
                position: 'absolute',
                top: -scrollOffset.y,
                width: GRID_COL_HEADER_WIDTH,
                height: timeLineScale.length * GRID_CELL_HEIGHT + GRID_HEADER_HEIGHT + GRID_CONTAINER_MARGIN_BOTTOM,
            } },
            React.createElement("div", { className: styles.gridColHaeder, style: {
                    position: 'absolute',
                    top: GRID_HEADER_HEIGHT,
                    width: GRID_COL_HEADER_WIDTH,
                    height: timeLineScale.length * GRID_CELL_HEIGHT,
                } }, timeLineScale.map((unit, index, arr) => (React.createElement("div", { key: unit, id: 'time' + unit, className: styles.gridColHaederCell, style: {
                    width: GRID_COL_HEADER_WIDTH,
                    position: 'absolute',
                    height: GRID_CELL_HEIGHT,
                    textAlign: 'center',
                    top: index * GRID_CELL_HEIGHT,
                } },
                React.createElement("span", null, index < arr.length - 1 && unit))))),
            isToday && (React.createElement("div", { id: "nowTitle", className: styles.nowTitle, style: { top: nowTop + GRID_HEADER_HEIGHT - 10, width: GRID_COL_HEADER_WIDTH } }, moment().format('HH:mm')))));
    };
    const renderGridCell = () => {
        const isToday = moment().isSame(currentDate, 'day');
        const dayStart = moment().startOf('day');
        const nowTop = moment().diff(dayStart, 'hour', true) * GRID_CELL_HEIGHT;
        return (React.createElement(React.Fragment, null,
            React.createElement("div", { className: styles.grid, style: {
                    display: 'grid',
                    gridTemplateColumns: `repeat(${userFreeBusy.length},${GRID_CELL_WIDTH}px)`,
                    gridTemplateRows: `repeat(24,${GRID_CELL_HEIGHT}px)`,
                } }, timeLineScale.map(key => (React.createElement(React.Fragment, { key: key }, userFreeBusy.map(item => (React.createElement("div", { key: item.account.accountId, className: classnames(styles.cell) }))))))),
            isToday && React.createElement("div", { id: "nowLine", className: styles.nowLine, style: { top: nowTop - 1, width: userFreeBusy.length * GRID_CELL_WIDTH } })));
    };
    const renderGridCorner = () => (React.createElement("div", { className: styles.gridCorner, style: {
            position: 'absolute',
            width: GRID_COL_HEADER_WIDTH,
            height: GRID_HEADER_HEIGHT,
        } },
        React.createElement("span", null, getIn18Text('QUANTIAN'))));
    const updateNow = () => {
        const cur = currentDate || startDate || moment();
        const isToday = cur.isSame(moment(), 'day');
        if (isToday) {
            const dayStart = moment().startOf('day');
            const nowTop = moment().diff(dayStart, 'hour', true) * GRID_CELL_HEIGHT;
            setTimeout(() => {
                if (document.getElementById('nowLine')) {
                    document.getElementById('nowLine').style.top = nowTop - 1 + 'px';
                }
                if (document.getElementById('nowTitle')) {
                    document.getElementById('nowTitle').style.top = nowTop + GRID_HEADER_HEIGHT - 10 + 'px';
                }
                if (document.getElementById('nowTitle')) {
                    document.getElementById('nowTitle').innerHTML = moment().format('HH:mm');
                }
            }, 0);
            const minute = moment().minute();
            if (minute > 50 || minute < 10) {
                const nearHour = moment().clone();
                nearHour.hour(minute > 50 ? moment().hour() + 1 : moment().hour());
                const timeStr = 'time' + nearHour.minute(0).format('HH:mm');
                const timeEl = document.getElementById(timeStr);
                if (timeEl) {
                    timeEl.style.opacity = '0';
                }
            }
            else {
                timeLineScale.forEach(t => {
                    document.getElementById('time' + t).style.opacity = '1';
                });
            }
        }
        else {
            timeLineScale.forEach(t => {
                document.getElementById('time' + t).style.opacity = '1';
            });
        }
    };
    useEffect(() => {
        if (startDate) {
            setCurrentDate(startDate);
        }
    }, [startDate]);
    useEffect(() => {
        var _a;
        if (!currentDate) {
            return;
        }
        const eventOrganizer = editingEvent === null || editingEvent === void 0 ? void 0 : editingEvent.scheduleInfo.organizer.extDesc;
        const currentUser = (_a = sysApi.getCurrentUser()) === null || _a === void 0 ? void 0 : _a.id;
        const userList = users.slice();
        if (eventOrganizer) {
            userList.unshift(eventOrganizer);
        }
        else if (currentUser) {
            userList.unshift(currentUser);
        }
        queryFreeBusyList({
            users: Array.from(new Set(userList)),
            start: currentDate.clone().startOf('day').toDate(),
            end: currentDate.clone().endOf('day').toDate(),
            uid,
        }).then(res => {
            setUserFreeBusy(sortByUser(res, [...new Set(userList)]));
        });
    }, [currentDate === null || currentDate === void 0 ? void 0 : currentDate.valueOf(), users]);
    useEffect(() => {
        var _a;
        const scrollInstance = (_a = scrollRef.current) === null || _a === void 0 ? void 0 : _a.osInstance();
        let y = 0;
        if (indicatorRange) {
            const [indicatorStart] = indicatorRange;
            const dayStart = (currentDate || currentMoment).clone().startOf('day');
            const distance = indicatorStart.diff(dayStart, 'hour', !0) * GRID_CELL_HEIGHT;
            y = distance;
        }
        if (scrollInstance) {
            scrollInstance.scroll({
                x: 0,
                y,
            });
        }
    }, [indicatorRange, currentDate]);
    useEffect(() => {
        updateNow();
        const timer = setInterval(() => {
            updateNow();
        }, 60 * 1000);
        return () => {
            clearInterval(timer);
        };
    }, [currentDate, GRID_HEADER_HEIGHT]);
    return (React.createElement("div", { ref: sizeRef, className: `${styles.container} dayviewtimelinegrid-container` },
        React.createElement(DateSwitch, { date: currentDate || currentMoment, onChange: setCurrentDate, onClose: onClose }),
        React.createElement("div", { className: styles.gridContainer, style: {
                width: `calc(100% - ${GRID_CONTAINER_MARGIN_RIGHT}px)`,
                position: 'relative',
                overflow: 'hidden',
                paddingBottom: GRID_CONTAINER_MARGIN_BOTTOM,
                marginRight: GRID_CONTAINER_MARGIN_RIGHT,
            } },
            renderGridRowHeader(),
            renderGirdColHeader(),
            renderGridCorner(),
            React.createElement(OverlayScrollbarsComponent, { ref: scrollRef, options: {
                    callbacks: {
                        onScroll: handleGridCellScroll,
                    },
                }, style: {
                    height: GRID_CONTAINER_HEIGHT - GRID_CONTAINER_MARGIN_BOTTOM - GRID_HEADER_HEIGHT,
                    width: containerWidth - GRID_CONTAINER_MARGIN_RIGHT - GRID_COL_HEADER_WIDTH,
                    left: GRID_COL_HEADER_WIDTH,
                    top: GRID_HEADER_HEIGHT,
                    position: 'absolute',
                } },
                renderIndicator(),
                renderBusyCell(),
                renderGridCell()))));
};
export default DayViewTimeLineGrid;
